// src/components/chatBox/MsgBox.jsx
import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';

const MsgBox = ({ sessionId, socket, openMsg, PerfectScrollbar, offMsg }) => {
   const [messages, setMessages] = useState([]);
   const [newMessage, setNewMessage] = useState('');
   const [isTyping, setIsTyping] = useState(false);
   const [error, setError] = useState(null);

   // Socket listeners
   useEffect(() => {
      if (!socket) return;

      socket.on('chatResponse', (response) => {
         // Example: server might send { response: "Is VAT applicable?", options: ["yes","no"], verification: null }
         if (response.error) {
            setError(response.error);
            return;
         }

         console.log(response);

         setMessages((prev) => [
            ...prev,
            {
               text: response.response,
               isBot: true,
               verification: response.verification,
               timestamp: new Date(),
               options: response.options || []  // <--- store possible choices
            }
         ]);
         setIsTyping(false);
         setError(null);
      });

      socket.on('connect_error', () => {
         setError('Connection lost. Please try again.');
      });

      return () => {
         socket.off('chatResponse');
         socket.off('connect_error');
      };
   }, [socket]);

   const sendMessage = () => {
      if (!newMessage.trim()) return;

      try {
         // Show user message
         setMessages(prev => [
            ...prev,
            {
               text: newMessage,
               isBot: false,
               timestamp: new Date()
            }
         ]);
         setIsTyping(true);

         // Send to server
         socket.emit('chatMessage', {
            userId: sessionId,
            message: newMessage.trim()
         });

         // Clear input
         setNewMessage('');
         setError(null);

      } catch (err) {
         setError('Failed to send message. Please try again.');
         setIsTyping(false);
      }
   };

   // If user clicks a multiple-choice option
   const handleOptionClick = (optionVal) => {

      console.log(optionVal);

      setMessages(prev => [
         ...prev,
         {
            text: optionVal,
            isBot: false,
            timestamp: new Date()
         }
      ]);
      setIsTyping(true);

      socket.emit('chatMessage', {
         userId: sessionId,
         message: optionVal
      });
   };

   // Scroll to bottom
   const messagesEndRef = useRef(null);
   const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
   };
   useEffect(scrollToBottom, [messages]);

   return (
      <div className={`card chat dlab-chat-history-box ${openMsg ? "" : "d-none"}`}>
         {/* Header */}
         <div className="card-header chat-list-header">
            <div className="d-flex align-items-center">
               <button className="btn btn-link text-dark p-0 me-3" onClick={offMsg}>
                  <i className="fas fa-arrow-left"></i>
               </button>
               <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faRobot} className="me-2" />
                  <div>
                     <h6 className="mb-0">Ledgy Assistant</h6>
                     <small className="text-success">Online</small>
                  </div>
               </div>
            </div>
         </div>

         {/* Messages */}
         <PerfectScrollbar className="card-body msg_card_body dlab-scroll">
            {error && <div className="alert alert-danger m-2">{error}</div>}

            {messages.map((msg, index) => (
               <div
                  key={index}
                  className={`d-flex ${msg.isBot ? 'justify-content-start' : 'justify-content-end'} mb-4`}
               >
                  {msg.isBot && (
                     <div className="img_cont_msg">
                        <FontAwesomeIcon icon={faRobot} className="rounded-circle user_img_msg" />
                     </div>
                  )}
                  <div className={msg.isBot ? 'msg_cotainer' : 'msg_cotainer_send'}>
                     {msg.text}

                     {/* If there's a verification object, show it */}
                     {msg.verification && (
                        <div className="verification-preview mt-2">
                           <div className="card bg-light">
                              <div className="card-body p-3">
                                 <h6 className="card-title mb-2 text-dark">Verification Created</h6>
                                 <div className="transactions-preview">
                                    {msg.verification.transactions.map((trans, idx) => (
                                       <div key={idx} className="transaction-item mb-2 bg-white p-2 rounded">
                                          <div className="d-flex justify-content-between">
                                             <span className="text-dark">Account {trans.account}</span>
                                             <span className={`${trans.amount > 0 ? 'text-success' : 'text-danger'}`}>
                                                {trans.amount.toFixed(2)} kr
                                             </span>
                                          </div>
                                          <small className="text-muted">{trans.description}</small>
                                       </div>
                                    ))}
                                 </div>
                                 <div className="mt-2 text-muted small">
                                    <div>ID: {msg.verification.id}</div>
                                    <div>Date: {msg.verification.date}</div>
                                    <div>Description: {msg.verification.description}</div>
                                 </div>
                              </div>
                           </div>
                        </div>
                     )}

                     {/* Render multiple-choice options if present */}
                     {msg.options && msg.options.length > 0 && (
                        <div className="mt-2">
                           {msg.options.map((optionVal) => (
                              <button
                                 key={optionVal}
                                 className="btn btn-xs btn-light me-2"
                                 onClick={() => handleOptionClick(optionVal)}
                              >
                                 {optionVal}
                              </button>
                           ))}
                        </div>
                     )}

                     <span className={msg.isBot ? 'msg_time' : 'msg_time_send'}>
                        {new Date(msg.timestamp).toLocaleTimeString()}
                     </span>
                  </div>
               </div>
            ))}

            {isTyping && (
               <div className="d-flex justify-content-start mb-4">
                  <div className="img_cont_msg">
                     <FontAwesomeIcon icon={faRobot} className="rounded-circle user_img_msg" />
                  </div>
                  <div className="msg_cotainer">
                     <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                     </div>
                  </div>
               </div>
            )}
            <div ref={messagesEndRef} />
         </PerfectScrollbar>

         {/* Input */}
         <div className="card-footer type_msg">
            <div className="input-group">
               <textarea
                  className="form-control"
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => {
                     if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                     }
                  }}
                  disabled={isTyping}
               />
               <button
                  className="btn btn-primary"
                  onClick={sendMessage}
                  disabled={isTyping || !newMessage.trim()}
               >
                  <i className="fas fa-paper-plane"></i>
               </button>
            </div>
         </div>
      </div>
   );
};

export default MsgBox;








// // src/components/chatBox/MsgBox.jsx
// import React, { useEffect, useRef, useState } from 'react';
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
// import { faRobot } from '@fortawesome/free-solid-svg-icons';


// const MsgBox = ({ sessionId, socket, openMsg, PerfectScrollbar, offMsg }) => {
//    const [messages, setMessages] = useState([]);
//    const [newMessage, setNewMessage] = useState('');
//    const [isTyping, setIsTyping] = useState(false);
//    const [error, setError] = useState(null);

//    // Set up socket listeners
//    useEffect(() => {
//       if (!socket) return;

//       socket.on('chatResponse', (response) => {
//          if (response.error) {
//             setError(response.error);
//             return;
//          }

//          setMessages(prev => [...prev, {
//             text: response.response,
//             isBot: true,
//             verification: response.verification,
//             timestamp: new Date()
//          }]);
//          setIsTyping(false);
//          setError(null);
//       });

//       socket.on('connect_error', () => {
//          setError('Connection lost. Please try again.');
//       });

//       return () => {
//          socket.off('chatResponse');
//          socket.off('connect_error');
//       };
//    }, [socket]);

//    const sendMessage = () => {
//       if (!newMessage.trim()) return;

//       try {
//          // Add user message to chat immediately
//          setMessages(prev => [...prev, {
//             text: newMessage,
//             isBot: false,
//             timestamp: new Date()
//          }]);

//          // Show typing indicator
//          setIsTyping(true);

//          // Send to server
//          socket.emit('chatMessage', {
//             userId: sessionId,
//             message: newMessage.trim()
//          });

//          // Clear input
//          setNewMessage('');
//          setError(null);

//       } catch (err) {
//          setError('Failed to send message. Please try again.');
//          setIsTyping(false);
//       }
//    };

//    // Add scroll to bottom when new messages arrive
//    const messagesEndRef = useRef(null);
//    const scrollToBottom = () => {
//       messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
//    };
//    useEffect(scrollToBottom, [messages]);

//    return (
//       <div className={`card chat dlab-chat-history-box ${openMsg ? "" : "d-none"}`}>
//          {/* Header */}
//          <div className="card-header chat-list-header">
//             <div className="d-flex align-items-center">
//                <button
//                   className="btn btn-link text-dark p-0 me-3"
//                   onClick={offMsg}
//                >
//                   <i className="fas fa-arrow-left"></i>
//                </button>
//                <div className="d-flex align-items-center">
//                   <FontAwesomeIcon icon={faRobot} className="me-2" />
//                   <div>
//                      <h6 className="mb-0">Ledgy Assistant</h6>
//                      <small className="text-success">Online</small>
//                   </div>
//                </div>
//             </div>
//          </div>

//          {/* Messages */}
//          <PerfectScrollbar className="card-body msg_card_body dlab-scroll">
//             {error && (
//                <div className="alert alert-danger m-2">{error}</div>
//             )}

//             {messages.map((msg, index) => (
//                <div key={index} className={`d-flex ${msg.isBot ? 'justify-content-start' : 'justify-content-end'} mb-4`}>
//                   {msg.isBot && (
//                      <div className="img_cont_msg">
//                         <FontAwesomeIcon icon={faRobot} className="rounded-circle user_img_msg" />
//                      </div>
//                   )}
//                   <div className={msg.isBot ? "msg_cotainer" : "msg_cotainer_send"}>
//                      {msg.text}
//                      {msg.verification && (
//                         <div className="verification-preview mt-2">
//                            <div className="card bg-light">
//                               <div className="card-body p-3">
//                                  <h6 className="card-title mb-2 text-dark">Verification Created</h6>
//                                  <div className="transactions-preview">
//                                     {msg.verification.transactions.map((trans, idx) => (
//                                        <div key={idx} className="transaction-item mb-2 bg-white p-2 rounded">
//                                           <div className="d-flex justify-content-between">
//                                              <span className="text-dark">Account {trans.account}</span>
//                                              <span className={`${trans.amount > 0 ? 'text-success' : 'text-danger'}`}>
//                                                 {trans.amount.toFixed(2)} kr
//                                              </span>
//                                           </div>
//                                           <small className="text-muted">{trans.description}</small>
//                                        </div>
//                                     ))}
//                                  </div>
//                                  <div className="mt-2 text-muted small">
//                                     <div>ID: {msg.verification.id}</div>
//                                     <div>Date: {msg.verification.date}</div>
//                                     <div>Description: {msg.verification.description}</div>
//                                  </div>
//                               </div>
//                            </div>
//                         </div>
//                      )}
//                      <span className={msg.isBot ? "msg_time" : "msg_time_send"}>
//                         {new Date(msg.timestamp).toLocaleTimeString()}
//                      </span>
//                   </div>
//                </div>
//             ))}

//             {isTyping && (
//                <div className="d-flex justify-content-start mb-4">
//                   <div className="img_cont_msg">
//                      <FontAwesomeIcon icon={faRobot} className="rounded-circle user_img_msg" />
//                   </div>
//                   <div className="msg_cotainer">
//                      <div className="typing-indicator">
//                         <span></span>
//                         <span></span>
//                         <span></span>
//                      </div>
//                   </div>
//                </div>
//             )}
//             <div ref={messagesEndRef} />
//          </PerfectScrollbar>

//          {/* Input */}
//          <div className="card-footer type_msg">
//             <div className="input-group">
//                <textarea
//                   className="form-control"
//                   placeholder="Type your message..."
//                   value={newMessage}
//                   onChange={(e) => setNewMessage(e.target.value)}
//                   onKeyPress={(e) => {
//                      if (e.key === 'Enter' && !e.shiftKey) {
//                         e.preventDefault();
//                         sendMessage();
//                      }
//                   }}
//                   disabled={isTyping}
//                />
//                <button
//                   className="btn btn-primary"
//                   onClick={sendMessage}
//                   disabled={isTyping || !newMessage.trim()}
//                >
//                   <i className="fas fa-paper-plane"></i>
//                </button>
//             </div>
//          </div>
//       </div>
//    );
// };

// export default MsgBox;